import { Button } from '@ads/components-react';
import { brandLinka } from '@ads/tokens';
import { DashboardDailySales } from '@linka/domain/dtos';
import { useTranslation } from '@linka/i18n';
import { formatDate, formatNumber } from '@linka/utils';
import { useNavigate } from 'react-router';
import { Bar, CartesianGrid, ComposedChart, LabelList, Line, XAxis, YAxis } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../../../chart/chart';
import {
  ChartCard,
  ChartCardContent,
  ChartCardFooter,
  ChartCardHeader,
  ChartCardTitle,
  ExportChartTriggerButton,
} from '../components';

interface SalesCountAndTotalByDayChartProps {
  data: DashboardDailySales;
}
export const SalesCountAndTotalByDayChart = ({ data }: SalesCountAndTotalByDayChartProps) => {
  const t = useTranslation();
  const navigate = useNavigate();
  const dailySalesConfig = {
    date: { label: t('date') },
    salesCount: { label: t('sales'), color: brandLinka.contextualColor.interactive.primary },
    totalAmount: { label: t('total-amount'), color: brandLinka.baseColor.brand['500'] },
  };
  return (
    <ChartCard className='lg:col-span-2'>
      <ChartCardHeader>
        <ChartCardTitle>{t('dashboard.charts.sales-vs-revenue-by-day')}</ChartCardTitle>
        <ExportChartTriggerButton type='OVERVIEW_SALES_COUNT_AND_TOTAL_BY_DAY' />
      </ChartCardHeader>
      {data.data.length === 0 ? (
        <ChartCardContent className='flex flex-1 items-center justify-center py-xl'>
          <span className='ts-paragraph-xs text-ctx-content-base'>{t('dashboard.charts.no-data')}</span>
        </ChartCardContent>
      ) : (
        <ChartCardContent className='flex'>
          <ChartContainer config={dailySalesConfig} className='w-[90px]'>
            <ComposedChart responsive accessibilityLayer data={data.data} margin={{ top: 24 }}>
              <Bar yAxisId='left' dataKey='salesCount' fill='transparent' barSize={0} />
              <YAxis
                dataKey='salesCount'
                orientation='left'
                yAxisId='left'
                tickLine={false}
                tickMargin={10}
                axisLine={false}
                width={90}
              />
            </ComposedChart>
          </ChartContainer>

          <div className='flex-1 overflow-x-auto pb-xs'>
            <ChartContainer config={dailySalesConfig} className='w-full' dataLength={data.data.length}>
              <ComposedChart responsive accessibilityLayer data={data.data} margin={{ top: 24, right: 24 }}>
                <CartesianGrid vertical={false} />
                <Line
                  yAxisId='right'
                  dataKey='totalAmount'
                  stroke='var(--color-totalAmount)'
                  fill='var(--color-totalAmount)'
                  dot={{
                    r: 2,
                    fill: 'var(--color-totalAmount)',
                  }}
                  activeDot={{
                    r: 5,
                  }}
                >
                  <LabelList
                    dataKey='totalAmount'
                    position='top'
                    fontSize={14}
                    offset={10}
                    formatter={(label) => formatNumber(Number(label))}
                    className='mix-blend-difference'
                    fill={brandLinka.baseColor.neutral.white}
                  />
                </Line>
                <Bar yAxisId='left' dataKey='salesCount' fill='var(--color-salesCount)' radius={[8, 8, 0, 0]}>
                  <LabelList dataKey='salesCount' position='bottom' />
                </Bar>
                <XAxis
                  dataKey='date'
                  tickLine={false}
                  tickMargin={10}
                  tick={false}
                  axisLine={false}
                  padding={{ left: 30, right: 30 }}
                />
                <ChartTooltip
                  labelFormatter={(label) => formatDate({ date: label, dateFormat: 'dd/MM/yyyy' }) ?? label}
                  cursor={{ fill: brandLinka.contextualColor.interactive.secondary }}
                  content={<ChartTooltipContent />}
                />
              </ComposedChart>
            </ChartContainer>
          </div>

          <ChartContainer config={dailySalesConfig} className='w-[90px]'>
            <ComposedChart responsive accessibilityLayer data={data.data} margin={{ top: 24 }}>
              <Line yAxisId='right' dataKey='totalAmount' stroke='transparent' fill='transparent' />
              <YAxis
                dataKey='totalAmount'
                orientation='right'
                yAxisId='right'
                tickLine={false}
                tickMargin={10}
                axisLine={false}
                tickFormatter={(value) => formatNumber(value, { notation: 'compact' })}
                width={90}
              />
            </ComposedChart>
          </ChartContainer>
        </ChartCardContent>
      )}
      {data.data.length > 0 && (
        <ChartCardFooter className='flex justify-end pt-mini *:!w-full sm:*:!w-auto'>
          <Button
            className='whitespace-nowrap !text-xxxs sm:!text-xxs'
            onClick={() => navigate(`detailed-sales${window.location.search}`)}
          >
            {t('dashboard.detailed.registrations-and-participants')}
          </Button>
        </ChartCardFooter>
      )}
    </ChartCard>
  );
};
